<template>
  <CoreSelectionDrawer
    has-search
    has-footer
    is-checked-variant
    :title="title"
    :title-truncate="titleTruncate"
    :placeholder="placeholder"
    :is-back="isBack"
    :value="value"
    :items="specialities"
    :selected-item="selectedSpeciality"
    :loading="loading"
    :error="error"
    attach=".vue-the-doctors-filters .v-application"
    @input="$emit('input', false)"
    @back="$emit('input', false)"
    @click-item="handleClickSpeciality"
  >
    <template #append-header>
      <v-btn
        v-if="selectedSpeciality && isVisibleReset"
        color="primary"
        small
        text
        class="ml-auto"
        @click="$emit('speciality:reset')"
      >
        {{ t('Сбросить', options) }}
      </v-btn>
    </template>
    <template #footer>
      <slot
        name="footer"
        v-bind="{ selectedSpeciality }"
      />
    </template>
  </CoreSelectionDrawer>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  toRefs,
  watch,
} from 'vue'
import I18NextVue from 'i18next'
import apiTownSpecialities from 'www/vue_components/common/TheFilters/doctors/api/apiTownSpecialities'
import CoreSelectionDrawer from 'src/core/CoreSelectionDrawer/CoreSelectionDrawer.vue'
import type { Filters } from 'www/vue_components/common/TheFilters/doctors/stores/useFiltersStore.types'

const emit = defineEmits(['input', 'speciality:selected', 'speciality:reset'])

const { t, language } = I18NextVue
const options = computed(() => ({ ns: language }))

const props = defineProps<{
  value: boolean
  isBack?: boolean
  title: string
  titleTruncate?: boolean
  placeholder?: string
  townId: number
  townTranslit: string
  isDoctorListOnline?: boolean
  isDoctorListHome?: boolean
  isVisibleReset?: boolean
  patientAge?: number,
  excludedSpeciality?: Filters['speciality'],
  selectedSpeciality: Filters['speciality'] | Filters['otherSpeciality']
}>()

const {
  value: isVisibleModal,
  isDoctorListOnline,
  isDoctorListHome,
  townId,
  patientAge,
  excludedSpeciality,
} = toRefs(props)

const loading = ref(false)
const error = ref(false)

const specialities = ref<Filters['speciality'][]>([])
const specialitiesCache: Map<string, Filters['speciality'][]> = new Map()

const loadSpecialities = async () => {
  error.value = false
  loading.value = true

  try {
    const requestData = {
      town_id: isDoctorListOnline?.value ? undefined : townId.value,
      is_house_filter: isDoctorListHome?.value || undefined,
      is_town_online_filter: isDoctorListOnline?.value || undefined,
    }
    const cacheKey = JSON.stringify(requestData)

    if (!specialitiesCache.has(cacheKey)) {
      const { data } = await apiTownSpecialities.get(requestData)
      const transformedSpecialities: Filters['speciality'][] = data.map(({ children, adult, ...rest }) => ({
        ...rest,
        ...(children && { children }),
        ...(adult && { adult }),
      }))

      specialitiesCache.set(cacheKey, transformedSpecialities)
    }

    specialities.value = (specialitiesCache.get(cacheKey) || [])
      .filter(spec => {
        if (excludedSpeciality?.value) {
          return spec.id !== excludedSpeciality.value.id // Показываем специальности, кроме исключённых
        }

        if (patientAge?.value !== undefined) {
          return patientAge.value >= 18
            ? (!!spec.children || !spec.adult) // Показываем только взрослые и общие специальности
            : (!!spec.adult || !spec.children) // Показываем только детские и общие специальности
        }

        return true // Показываем все специальности
      })
  } catch {
    error.value = true
  } finally {
    loading.value = false
  }
}

watch(isVisibleModal, async () => {
  if (isVisibleModal.value) {
    await loadSpecialities()
  }
})

const handleClickSpeciality = (speciality: Filters['speciality']) => {
  emit('speciality:selected', speciality)
}
</script>
